import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootReducerState } from '../../redux/Reducers';
import { rejectWithApiError } from '../../utils/api';
import { BillProfile, BillingCredentialsTemplate } from './billProfiles.slice';
import { RevioAccount } from './billProfiles.types';
import { CreateBillingCredentialsDto } from '../../types/billing-credentials/billing-credentials';
import { PaginationType } from '../../types/utils/pagination.type';
import { RouteParamsType } from '../../types/utils/router-params.type';

export const fetchBillProfiles = createAsyncThunk(
  'billProfiles/fetch',
  async (params?: { revioAccount?: RevioAccount; searchQuery?: string }) => {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/bill-profiles/list`, {
      params: params
        ? {
            revioAccount: params.revioAccount,
            q: params.searchQuery,
          }
        : undefined,
    });
    return data as BillProfile[];
  }
);

export const fetchRevioBillProfiles = createAsyncThunk('billProfiles/fetchRevio', async () => {
  const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/bill-profiles/revio/list`, {
    params: { revioAccount: RevioAccount.TBCRESELLER },
  });
  return data as any[];
});

export const importBillProfile = createAsyncThunk(
  'billProfiles/import',
  async (payload: { revioId: number; name: string; revioAccount?: RevioAccount }) => {
    const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/bill-profiles/revio/import`, {
      revioAccount: payload.revioAccount || RevioAccount.TBCRESELLER,
      revioId: payload.revioId,
      name: payload.name,
    });
    return data as BillProfile;
  }
);

export const updateBillProfile = createAsyncThunk(
  'billProfiles/update',
  async ({ id, changes }: { id: number; changes: Partial<BillProfile> }) => {
    const { data } = await axios.patch(`${process.env.REACT_APP_API_URL}/bill-profiles/${id}`, changes);
    return data as BillProfile;
  }
);

export const getBillProfileDetails = createAsyncThunk<
  BillProfile,
  number,
  { state: RootReducerState; rejectValue: string }
>('billProfiles/getDetails', async (id: number, thunkAPI) => {
  try {
    const { data } = await axios.get<BillProfile>(`${process.env.REACT_APP_API_URL}/bill-profiles/${id}`);
    return data;
  } catch (error) {
    return rejectWithApiError({ error, thunkAPI, fallback: 'Could not fetch Bill Profile details!' });
  }
});

export const deleteBillProfile = createAsyncThunk('billProfiles/delete', async (id: number) => {
  await axios.delete(`${process.env.REACT_APP_API_URL}/bill-profiles/${id}`);
  return id;
});

export const getBillingCredentials = createAsyncThunk<
  PaginationType<BillingCredentialsTemplate>,
  Partial<RouteParamsType> | void,
  { state: RootReducerState; rejectValue: string }
>('billProfiles/fetchBillingCredentials', async (routeParams, thunkAPI) => {
  try {
    const params = routeParams
      ? {
          page: routeParams.page ?? 1,
          limit: routeParams.limit ?? 10,
          order: routeParams.order ?? '-createdAt',
          q: routeParams.q,
        }
      : { page: 1, limit: 100, order: '-createdAt' };
    const { data } = await axios.get<PaginationType<BillingCredentialsTemplate>>(
      `${process.env.REACT_APP_API_URL}/bill-profiles/billing-credentials`,
      { params }
    );
    return data;
  } catch (error) {
    return rejectWithApiError({ error, thunkAPI, fallback: 'Could not fetch Billing Credentials!' });
  }
});

export const createBillingCredentials = createAsyncThunk<
  BillingCredentialsTemplate,
  CreateBillingCredentialsDto,
  { state: RootReducerState; rejectValue: string }
>('billProfiles/createBillingCredentials', async (payload, thunkAPI) => {
  try {
    const { data } = await axios.post<BillingCredentialsTemplate>(
      `${process.env.REACT_APP_API_URL}/bill-profiles/billing-credentials`,
      payload
    );
    return data;
  } catch (error) {
    return rejectWithApiError({ error, thunkAPI, fallback: 'Could not create Bill credentials!' });
  }
});

export const deleteBillingCredentials = createAsyncThunk<
  number,
  number,
  { state: RootReducerState; rejectValue: string }
>('billProfiles/deleteBillingCredentials', async (id, thunkAPI) => {
  try {
    await axios.delete(`${process.env.REACT_APP_API_URL}/bill-profiles/billing-credentials/${id}`);
    return id;
  } catch (error) {
    return rejectWithApiError({ error, thunkAPI, fallback: 'Could not delete Bill credentials!' });
  }
});

export const assignBillingCredentials = createAsyncThunk<
  BillProfile,
  { billProfileId: number; billingCredentialsId: number },
  { state: RootReducerState; rejectValue: string }
>('billProfiles/assignBillingCredentials', async (payload, thunkAPI) => {
  try {
    const { data } = await axios.post<BillProfile>(
      `${process.env.REACT_APP_API_URL}/bill-profiles/billing-credentials/assign`,
      payload
    );
    return data;
  } catch (error) {
    return rejectWithApiError({ error, thunkAPI, fallback: 'Could not assign Billing credentials!' });
  }
});
